import React, { useEffect, useState } from "react";
import Select from "react-select";
import Banner from "../../../../ui/Banner/Banner";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";

interface RecurringTaskCreationDateStepProps {
  step: number;
  setValue: (calculationType: string, offset: string) => void;
}

// Define the option type
type OptionType = {
  value: string;
  label: string;
};

const RecurringTaskCreationDateStep: React.FC<RecurringTaskCreationDateStepProps> = ({
  step,
  setValue,
}) => {
  const [selectedOption, setSelectedOption] = useState<OptionType | null>();
  const [fixedDateSelection, setFixedDateSelection] =
    useState<OptionType | null>(null);
  const [dayInput, setDayInput] = useState<string | "">("");

  // Define the options
  const options: OptionType[] = [
    { value: "daysAfterCreation", label: "Days After Creation" },
    { value: "daysAfterLinkageDate", label: "Days After Linkage Date" },
    { value: "daysAfterCpocDate", label: "Days After CPOC Date" },
    { value: "daysAfterCpocEndDate", label: "Days After CPOC End Date" },
    { value: "daysBeforeCreation", label: "Days Before Creation" },
    { value: "daysBeforeLinkageDate", label: "Days Before Linkage Date" },
    { value: "daysBeforeCpocDate", label: "Days Before CPOC Date" },
    { value: "daysBeforeCpocEndDate", label: "Days Before CPOC End Date" },
    { value: "monthsAfterCreation", label: "Months After Creation" },
    { value: "monthsAfterLinkageDate", label: "Months After Linkage Date" },
    { value: "monthsAfterCpocDate", label: "Months After CPOC Date" },
    { value: "monthsAfterCpocEndDate", label: "Months After CPOC End Date" },
    { value: "monthsBeforeCreation", label: "Months Before Creation" },
    { value: "monthsBeforeLinkageDate", label: "Months Before Linkage Date" },
    { value: "monthsBeforeCpocDate", label: "Months Before CPOC Date" },
    { value: "monthsBeforeCpocEndDate", label: "Months Before CPOC End Date" },
    { value: "fixedDate", label: "Fixed Date" },
  ];

  const fixedDateOptions: OptionType[] = [
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
  ];

  const handleChange = (option: OptionType | null) => {
    setSelectedOption(option);
    // Handle change here (e.g., storing the selected value)
  };

  const handleFixedDateChange = (option: OptionType | null) => {
    setFixedDateSelection(option);
    setDayInput("");
  };

  const handleDayInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDayInput(event.target.value);
  };

  const handleOffsetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDayInput(event.target.value);
  };

  const isValidDayInput = (): boolean => {
    const day = parseInt(dayInput);
    if (isNaN(day)) return false;
    if (selectedOption?.value === "fixedDate" && fixedDateSelection?.value === "weekly") {
      return day >= 1 && day <= 7;
    } else if (selectedOption?.value === "fixedDate") {
      return day >= 1 && day <= 31;
    } else { // for non-fixed dates
      return day >= 0 && day <= 365;
    }
  };

  const handleSubmit = () => {
    if (!isValidDayInput()) {
        toast.error("Please Enter the Valid date");
      return;
    }
    if (selectedOption?.value === "fixedDate" && fixedDateSelection) {
      const day = parseInt(dayInput);
      let cronExpression = "";

      if (fixedDateSelection.value === "weekly") {
        const cronDay = day % 7; // Converts 7 to 0 (Sunday) and keeps other days as is
        cronExpression = `0 0 * * ${cronDay}`;
      } else if (fixedDateSelection.value === "monthly" && day) {
        cronExpression = `0 0 ${day} * *`;
      } else if (fixedDateSelection.value === "quarterly" && day) {
        cronExpression = `0 0 ${day} */3 *`;
      } else if (fixedDateSelection.value === "yearly" && day) {
        cronExpression = `0 0 ${day} 1 *`;
      } else {
        return;
      }

      setValue(selectedOption.value, cronExpression);

    } else if (selectedOption?.value !== undefined) {
      setValue(selectedOption?.value, dayInput);
    }
  };

  return (
    <>
      <div className="top border_tittle mby_16">
        <div className="top_bar_tittle" style={{ display: 'unset' }}>
          <h2>Great, now the same thing, but for when the task should be created (visible)!{" "}</h2>
          <p>How often would you like this task to be created?</p>
        </div>
      </div>
      <div>
        <div className="custom_input_flled">
          <label>Select the calculation type for the creation date</label>
          <Select
            value={selectedOption}
            onChange={handleChange}
            options={options}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
      </div>
      <div>
        {selectedOption?.value === "fixedDate" ? (
          <div className="custom_input_flled">
            <label>Please select a fixed date</label>
            <Select
              value={fixedDateSelection}
              onChange={handleFixedDateChange}
              options={fixedDateOptions}
              className="react-select-container"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {fixedDateSelection && (
              <div className="mt-5 flex-col">
                <div className="text-sm mb-1">Select an day of the week/month</div>
                <input
                  className="w-full border p-2 rounded-md"
                  type="number"
                  min={fixedDateSelection.value === "weekly" ? "1" : "1"}
                  max={fixedDateSelection.value === "weekly" ? "7" : "31"}
                  value={dayInput}
                  onChange={handleDayInputChange}
                  placeholder={
                    fixedDateSelection.value === "weekly"
                      ? "Day of the week (1=Monday, 7=Sunday)"
                      : "Day of the month"
                  }
                />
              </div>
            )}
          </div>
        ) : (
          selectedOption && (
            <div className="custom_input_flled mb-0">
              <label>Input an offset in days</label>
              <input
                type="number"
                min="0"
                max="365"
                step="1"
                placeholder="Enter a positive integer"
                onChange={handleOffsetChange}
              // Include more input handling as needed
              />
            </div>
          )
        )}
      </div>
      <div className="form_btn_flx mby_t_24">
        {selectedOption && (
          <Button variant="primary" className="block ms-auto primary-button" onClick={handleSubmit}>Next</Button>
        )}
      </div>
    </>
  );
};

export default RecurringTaskCreationDateStep;
